import { DayCapacity_Input, Inventory, OptimizePlanResponse, PlanDay_Output, PlanItemState_Output, ProcessTime, ProductionQuantity_Output, ShipmentQuantity } from '@/api'

export type NormalizedOptimizePlanResponse = OptimizePlanResponse & {
  dates: string[]
  capacities: DayCapacity_Input[]
  processTimes: ProcessTime[]
  productions: Record<string, ProductionQuantity_Output[]>
  inventories: Record<string, Inventory[]>
  shipments: Record<string, ShipmentQuantity[]>
}

const ensureProcessTime = (processTime?: ProcessTime): ProcessTime => {
  if (processTime && typeof processTime.value === 'number') {
    return processTime
  }
  return { value: 0 }
}

const ensureProduction = (state?: PlanItemState_Output): ProductionQuantity_Output => {
  if (state?.production) {
    return state.production
  }
  return { value: 0, is_bold: false }
}

const ensureInventory = (state?: PlanItemState_Output): Inventory => {
  if (state?.inventory) {
    return state.inventory
  }
  return { value: 0 }
}

const ensureShipment = (state?: PlanItemState_Output): ShipmentQuantity => {
  if (state?.shipment) {
    return state.shipment
  }
  return { value: 0 }
}

const ensureCapacity = (capacity?: DayCapacity_Input): DayCapacity_Input => {
  if (capacity) {
    return {
      ...capacity,
      amount: typeof capacity.amount === 'number' ? capacity.amount : 3000,
    }
  }
  return {
    capacity: { value: 0 },
    lines: [],
    amount: 3000,
  }
}

export const normalizeOptimizePlanResponse = (
  response: OptimizePlanResponse,
): NormalizedOptimizePlanResponse => {
  const days: PlanDay_Output[] = response.days ?? []
  const dates = days.map((day) => day.date)
  const capacities = days.map((day) => ensureCapacity(day.capacity))
  const processTimes = days.map((day) => ensureProcessTime(day.processTime))
  const itemIds = new Set<string>()
  days.forEach((day) => {
    Object.keys(day.items ?? {}).forEach((itemId) => itemIds.add(itemId))
  })

  const productions: Record<string, ProductionQuantity_Output[]> = {}
  const inventories: Record<string, Inventory[]> = {}
  const shipments: Record<string, ShipmentQuantity[]> = {}

  itemIds.forEach((itemId) => {
    productions[itemId] = days.map((day) => ensureProduction(day.items[itemId]))
    inventories[itemId] = days.map((day) => ensureInventory(day.items[itemId]))
    shipments[itemId] = days.map((day) => ensureShipment(day.items[itemId]))
  })

  return {
    ...response,
    dates,
    capacities,
    processTimes,
    productions,
    inventories,
    shipments,
  }
}
